/* ============================================================
   SAM-198 (mock 7b) — the sections of "New business": Identity, where it appears in the app (category +
   subcategory) and Location (SAM-1237). The admin's New business page and the owner's «Register your business»
   (SAM-1298, W3 = the same form without the menu) share them.
   New file under the BaseUI/Styletron freeze (SAM-175): Tailwind + tokens.
   ============================================================ */
import React, { useMemo } from 'react';
import { useBusinessCategories, useBusinessTypes } from '../../hooks/useBusinessCrud';
import { S3ImageUpload } from '../../components/S3ImageUpload';
import { PhoneInput } from '../../components/ui/PhoneInput';
import { useTenantBranding } from '../../hooks/useTenantBranding';
import { BusinessLocationFields } from './location/BusinessLocationFields';
import { newBusinessIssues, type NewBusinessForm } from './newBusiness';

export const newBusinessCard = 'bg-white border border-solid border-[var(--line)] rounded-[12px] px-4 sm:px-6 py-5';
const card = newBusinessCard;

export const NewBusinessFields: React.FC<{
  form: NewBusinessForm;
  setForm: React.Dispatch<React.SetStateAction<NewBusinessForm>>;
  touched: boolean;
  /** Rendered at the end of "Where it appears in the app" (W3: «Which estate group?» for an Estate business). */
  categoryExtra?: React.ReactNode;
}> = ({ form, setForm, touched, categoryExtra }) => {
  const { data: categories = [] } = useBusinessCategories();
  const { data: businessTypes = [] } = useBusinessTypes();
  const { tenantName } = useTenantBranding();
  const issues = newBusinessIssues(form);
  const showIssue = (k: (typeof issues)[number]) => touched && issues.includes(k);
  const set = <K extends keyof NewBusinessForm>(key: K, value: NewBusinessForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));
  const subcategories = useMemo(
    () => businessTypes.filter((t) => t.business_category_id === form.business_category_id),
    [businessTypes, form.business_category_id],
  );

  return (
    <>
      <section aria-labelledby="nb-identity" className={card}>
        <h2 id="nb-identity" className="text-[16px] font-bold mb-4 text-[var(--ink-900)]">Identity</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
          <div className={`fc ${showIssue('name') ? 'err' : ''}`} style={{ marginBottom: 14 }}>
            <label htmlFor="nb-name">Name<span className="req"> *</span></label>
            <input
              id="nb-name"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="e.g. Api Beach"
            />
            {showIssue('name') && <div className="msg" style={{ display: 'block' }}>Enter the business name</div>}
          </div>
          <div className="fc" style={{ marginBottom: 14 }}>
            <label>Logo</label>
            <S3ImageUpload
              label="Logo"
              value={form.logo_url}
              onChange={(key) => set('logo_url', key)}
              variant="slot"
              slotWidth="100%"
              slotHeight="88px"
              objectFit="contain"
              placeholder="Upload logo"
            />
          </div>
          <div className={`fc ${showIssue('email') ? 'err' : ''}`} style={{ marginBottom: 14 }}>
            <label htmlFor="nb-email">Email</label>
            <input
              id="nb-email"
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              placeholder="reservas@…"
            />
            {showIssue('email') && <div className="msg" style={{ display: 'block' }}>Enter a valid email</div>}
          </div>
          <div className="fc" style={{ marginBottom: 14 }}>
            <label>Phone</label>
            <PhoneInput value={form.phone_number} onChange={(phone) => set('phone_number', phone)} />
          </div>
        </div>
      </section>

      <section aria-labelledby="nb-category" className={card}>
        <h2 id="nb-category" className="text-[16px] font-bold text-[var(--ink-900)]">Where it appears in the app</h2>
        <p className="text-[13px] mt-1 mb-4 text-[var(--ink-500)]">
          The category belongs to the business. All its services show up under it.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
          <div className="fc" style={{ marginBottom: 14 }}>
            <label htmlFor="nb-cat">Category</label>
            <select
              id="nb-cat"
              value={form.business_category_id ?? ''}
              onChange={(e) => setForm((prev) => ({
                ...prev,
                business_category_id: e.target.value === '' ? null : Number(e.target.value),
                business_type_id: null,
              }))}
            >
              <option value="">Select a category…</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className={`fc ${showIssue('subcategory') ? 'err' : ''}`} style={{ marginBottom: 14 }}>
            <label htmlFor="nb-sub">
              Subcategory{form.business_category_id != null && <span className="req"> *</span>}
            </label>
            <select
              id="nb-sub"
              value={form.business_type_id ?? ''}
              disabled={form.business_category_id == null}
              onChange={(e) => set('business_type_id', e.target.value === '' ? null : Number(e.target.value))}
            >
              <option value="">{form.business_category_id == null ? '—' : 'Select a subcategory…'}</option>
              {subcategories.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            {showIssue('subcategory') && <div className="msg" style={{ display: 'block' }}>Pick a subcategory</div>}
          </div>
        </div>
        {categoryExtra}
      </section>

      <section aria-labelledby="nb-location" className={card}>
        <h2 id="nb-location" className="text-[16px] font-bold text-[var(--ink-900)]">Location</h2>
        <p className="text-[13px] mt-1 mb-4 text-[var(--ink-500)]">Shown in the business profile in the app.</p>
        <BusinessLocationFields
          value={form.location}
          onChange={(location) => set('location', location)}
          tenantName={tenantName}
          showIssues={touched}
        />
      </section>
    </>
  );
};
